import useSWR from "swr";
import { useEffect, useMemo, useRef } from "react";
import Router from "next/router";
import { JSON_FETCHER } from "@/components/fetcher";
import { type Subscription, subscriptionPlan } from "@/utils/billing";
import { trackAbVariant } from "@/utils/analytics";
import { Region } from "@/components/models/Monitor";
import { TeamInfo, TeamRole } from "./useTeamMembers";

type StripeSubscription = {
  status: "active" | "canceled" | "ended" | "past_due" | "trialing" | "unpaid";
  amount: number;
  currency: string;
  current_period_end: number;
  trial_end?: number | null;
};

type Settings = {
  google_oauth_enabled: boolean;
  apple_oauth_enabled: boolean;
};

export type AccessibleAccount = {
  id: string;
  email: string;
  team_name: string | null;
  role: TeamRole;
};

type AccountInfo = {
  id: string;
  email: string;
  status: string;
  down_count: number;
  prepaid_sms_messages: number;
  prepaid_email_messages: number;
  created_at: number;
  current_payment_method: string | null;
  source_platform: "stripe" | "heroku" | "aws";
  current_plan: "10" | "50" | "100" | "200" | "500" | "1000";
  timezone?: string | null;
  is_admin?: boolean;
  require_payment_method?: boolean;
  pending_invoice_url?: string | null;

  current_subscription: Subscription;
  subscription: StripeSubscription;
  regions: Region[];
};

export default function useCurrentAccount(params: { requireLoggedIn?: boolean } = {}): {
  account: AccountInfo;
  settings: Settings;
  team: TeamInfo | null;
  accessibleAccounts: AccessibleAccount[];
  isLoading: boolean;
  isError: any;
} {
  const { data, error } = useSWR("/web_api/accounts/current", JSON_FETCHER);

  const { requireLoggedIn } = params || {};

  useEffect(() => {
    if (requireLoggedIn && data && !data.account) {
      Router.push("/accounts/login");
    }
  }, [data, requireLoggedIn]);

  useEffect(() => {
    if (
      data?.account?.require_payment_method &&
      !data?.account?.current_payment_method &&
      data?.team?.role === "admin"
    ) {
      const path = Router.asPath;
      const isExcluded =
        path.startsWith("/dashboard/billing") ||
        path.startsWith("/dashboard/settings") ||
        path.startsWith("/accounts");
      if (!isExcluded) {
        Router.push("/dashboard/billing/add-payment-method");
      }
    }
  }, [data]);

  const abVariantTracked = useRef(false);
  useEffect(() => {
    if (data?.account && !abVariantTracked.current) {
      abVariantTracked.current = true;
      trackAbVariant(!!data.account.require_payment_method);
    }
  }, [data]);

  const account = useMemo(() => {
    if (!data?.account) return data?.account;
    return {
      ...data.account,
      current_subscription: subscriptionPlan(data.account.current_plan),
    };
  }, [data]);

  return {
    account,
    settings: data?.settings,
    team: data?.team || null,
    accessibleAccounts: data?.accessible_accounts || [],
    isLoading: !(data || error),
    isError: error,
  };
}
